import {
  Activity,
  BookOpen,
  Bot,
  CalendarClock,
  CreditCard,
  History,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  Plug,
  Puzzle,
  ShieldCheck,
  Sun,
  Users,
} from "lucide-react"
import * as React from "react"
import { NavLink, Outlet } from "react-router-dom"

import { LanguageSwitcher } from "@/components/language-switcher"
import { useTheme } from "@/components/theme-provider"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { WorkspaceSwitcher } from "@/components/workspace-switcher"
import { useT } from "@/i18n/provider"
import { useAuth } from "@/lib/auth"
import { cn } from "@/lib/utils"

const NAV = [
  { to: "/guide", label: "nav.guide", icon: BookOpen },
  { to: "/chat", label: "nav.chat", icon: MessageSquare },
  { to: "/agents", label: "nav.agents", icon: Bot },
  { to: "/connections", label: "nav.toolServers", icon: Plug },
  { to: "/plugins", label: "nav.plugins", icon: Puzzle },
  { to: "/schedules", label: "nav.schedules", icon: CalendarClock },
  { to: "/runs", label: "nav.runs", icon: Activity },
  { to: "/activity", label: "nav.activity", icon: History },
  { to: "/workspace", label: "nav.workspace", icon: Users },
  { to: "/billing", label: "nav.billing", icon: CreditCard },
]

export function Layout() {
  const { user, signOut } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const t = useT()
  const [open, setOpen] = React.useState(false)
  const navItems = user?.is_admin
    ? [...NAV, { to: "/admin", label: "nav.admin", icon: ShieldCheck }]
    : NAV

  return (
    <div className="bg-background flex h-full">
      <aside
        className={cn(
          "bg-card flex w-60 shrink-0 flex-col border-r",
          "max-md:fixed max-md:inset-y-0 max-md:z-40 max-md:transition-transform",
          open ? "max-md:translate-x-0" : "max-md:-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center gap-2 px-4">
          <Bot className="size-5" />
          <span className="font-semibold">AI Agents</span>
        </div>
        <div className="px-3 pb-3">
          <WorkspaceSwitcher />
        </div>
        <Separator />
        <nav className="flex-1 space-y-1 p-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                )
              }
            >
              <item.icon className="size-4" />
              {t(item.label)}
            </NavLink>
          ))}
        </nav>
        <Separator />
        <div className="space-y-2 p-3">
          <div className="px-1 text-xs">
            {user?.display_name && <div className="truncate font-medium">{user.display_name}</div>}
            <div className="text-muted-foreground truncate" title={user?.email}>
              {user?.email}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
            >
              {resolvedTheme === "dark" ? (
                <Sun className="size-4" />
              ) : (
                <Moon className="size-4" />
              )}
              {t("nav.theme")}
            </Button>
            <LanguageSwitcher compact />
            <Button variant="outline" size="sm" onClick={signOut} aria-label={t("nav.signOut")}>
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>
      </aside>

      {open && (
        <button
          aria-label={t("nav.closeMenu")}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b px-4 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOpen(true)}
            aria-label={t("nav.openMenu")}
          >
            <Menu className="size-5" />
          </Button>
          <span className="font-semibold">AI Agents</span>
        </header>
        <main className="min-h-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 border-b px-6 py-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
      </div>
      {action}
    </div>
  )
}
